import React, {
  createContext,
  useContext,
  useEffect,
  ReactNode,
  useRef,
  useState,
  useCallback,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useCookie } from './CookieContext';

interface AnalyticsContextType {
  trackPageView: (page: string, title?: string) => void;
  trackEvent: (eventName: string, parameters?: Record<string, any>) => void;
  trackUserAction: (action: string, category: string, label?: string, value?: number) => void;
  trackConversion: (conversionType: string, value?: number) => void;
}

type AnalyticsModule = typeof import('../utils/analytics');

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

interface AnalyticsProviderProps {
  children: ReactNode;
  trackingId?: string;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ 
  children, 
  trackingId = import.meta.env.VITE_GA_TRACKING_ID 
}) => {
  const location = useLocation();
  const { preferences } = useCookie();
  const analyticsModuleRef = useRef<AnalyticsModule | null>(null);
  const [moduleReady, setModuleReady] = useState(false);

  const loadAnalyticsModule = useCallback(async () => {
    if (!preferences.analytics || analyticsModuleRef.current || !trackingId) {
      if (analyticsModuleRef.current) {
        setModuleReady(true);
      }
      return;
    }

    try {
      const module = await import('../utils/analytics');
      analyticsModuleRef.current = module;
      module.initializeAnalytics?.(trackingId);
      setModuleReady(true);
    } catch (error) {
      console.error('Failed to load analytics module:', error);
    }
  }, [preferences.analytics, trackingId]);

  // Initialize analytics when component mounts
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!cancelled) {
        await loadAnalyticsModule();
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [loadAnalyticsModule]);

  // Track page views on route changes
  useEffect(() => {
    if (preferences.analytics && moduleReady && analyticsModuleRef.current) {
      const page = location.pathname + location.search;
      const title = document.title;
      analyticsModuleRef.current.trackPageView(page, title);
    }
  }, [location, preferences.analytics, moduleReady]);

  const safeTrackEvent = useCallback(
    (eventName: string, parameters?: Record<string, any>) => {
      if (preferences.analytics && analyticsModuleRef.current) {
        analyticsModuleRef.current.trackEvent(eventName, parameters);
      }
    },
    [preferences.analytics]
  );

  const trackUserAction = (action: string, category: string, label?: string, value?: number) => {
    if (preferences.analytics) {
      safeTrackEvent('user_action', {
        action,
        category,
        label,
        value,
        page: location.pathname,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const trackConversion = (conversionType: string, value?: number) => {
    if (preferences.analytics) {
      safeTrackEvent('conversion', {
        conversion_type: conversionType,
        value,
        page: location.pathname,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const value: AnalyticsContextType = {
    trackPageView: (page: string, title?: string) => {
      if (preferences.analytics && analyticsModuleRef.current) {
        analyticsModuleRef.current.trackPageView(page, title);
      }
    },
    trackEvent: (eventName: string, parameters?: Record<string, any>) => {
      safeTrackEvent(eventName, parameters);
    },
    trackUserAction,
    trackConversion,
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};
